import { Dispatch, SetStateAction, useCallback } from "react"
import {
  Block,
  Divider,
  GasFee,
  ParagraphSmall,
  ShieldZap,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { selectUpdateCollateralStrategy, useDappSelector } from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import {
  useChangeInLiquidationPriceOnCollateralChange,
  useCollateralPrice,
  useManageCollateralAmount,
  useManageCollateralRatio,
} from "#/shared/hooks"
import {
  BorrowCollateralManagementActionUnit,
  BorrowCollateralManagementStrategy,
} from "#/shared/types"
import { floatToBigInt, getCurrencySymbol } from "#/shared/utils"
import { ComponentStepProps } from "#/ui/Modal"

import {
  BorrowProgress,
  BorrowReviewBox,
  BorrowReviewListItem,
  BorrowTerms,
} from "../components"

type ManageReviewProps = ComponentStepProps & {
  handleManageCollateral: () => Promise<void>
  setHasAcceptedTerms: Dispatch<SetStateAction<boolean>>
  hasAcceptedTerms: boolean
  activeSetupAction: BorrowCollateralManagementActionUnit
}
export default function ManageReview({
  onClickContinue,
  handleManageCollateral,
  setHasAcceptedTerms,
  hasAcceptedTerms,
  activeSetupAction,
}: ManageReviewProps) {
  // const wallet = useOnboardingWallet()
  const [css, theme] = useStyletron()

  const strategy = useDappSelector(selectUpdateCollateralStrategy)

  const {
    updateCollateralStrategy,
    updateCollateralAmount,
    prevCollateralAmountLabel,
    newCollateralAmountLabel,
    prevCollateralAmount,
    newCollateralAmount,
  } = useManageCollateralAmount(activeSetupAction)

  const {
    prevCollateralizationRatioLabel,
    newCollateralizationRatioLabel,
    collateralizationRatioChangeLabel,
  } = useManageCollateralRatio()

  const { collateralPriceLabelWithUSD } = useCollateralPrice(
    floatToBigInt(updateCollateralAmount),
  )

  const {
    newLiquidationPriceLabel,
    changeInLiquidationPriceLabelWithUSD,
    prevLiquidationPriceLabel,
  } = useChangeInLiquidationPriceOnCollateralChange(
    prevCollateralAmount,
    newCollateralAmount,
  )

  const isDepositingCollateral =
    updateCollateralStrategy ===
    BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL

  const onSubmit = useCallback(() => {
    handleManageCollateral()
    onClickContinue()
  }, [handleManageCollateral, onClickContinue])

  return (
    <Block flex={1}>
      <BorrowProgress step={2} stepsCount={4} />
      <Block
        className={css({
          position: "relative",
          padding: theme.sizing.scale800,
          paddingTop: 0,
          paddingBottom: "170px",
          overflow: "hidden",
          height: "calc(100% - 24px)",
        })}
      >
        <Block
          className={css({
            flexDirection: "column",
            display: "flex",
            gap: theme.sizing.scale800,
            height: "100%",
            paddingBottom: theme.sizing.scale800,
            overflow: "scroll",
            scrollbarWidth: "none",
          })}
        >
          <BorrowReviewBox>
            <BorrowReviewListItem
              header="Total collateral"
              endHeader={`${newCollateralAmountLabel} ${getCurrencySymbol(
                "mBTC",
              )}`}
              description={
                updateCollateralAmount
                  ? `${
                      isDepositingCollateral ? "+" : "-"
                    }${updateCollateralAmount} ${getCurrencySymbol("mBTC")}`
                  : undefined
              }
              endDescription={
                updateCollateralAmount
                  ? `from ${prevCollateralAmountLabel} ${getCurrencySymbol(
                      "mBTC",
                    )}`
                  : undefined
              }
              hasBorder
            />
            <BorrowReviewListItem
              header="Collateralization ratio"
              endHeader={`${newCollateralizationRatioLabel}%`}
              description={
                <>
                  {updateCollateralAmount
                    ? `${
                        isDepositingCollateral ? "+" : ""
                      }${collateralizationRatioChangeLabel}%`
                    : undefined}
                  {strategy ===
                    BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL && (
                    <Block
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        gap: theme.sizing.scale100,
                      })}
                    >
                      Increased safety
                      <Block marginTop={theme.sizing.scale100}>
                        <ShieldZap
                          size={14}
                          color={theme.colors.backgroundAccent}
                        />
                      </Block>
                    </Block>
                  )}
                </>
              }
              endDescription={
                updateCollateralAmount
                  ? `from ${prevCollateralizationRatioLabel}%`
                  : undefined
              }
            />
          </BorrowReviewBox>
          <BorrowReviewBox>
            <BorrowReviewListItem
              header="New liquidation price"
              endHeader={`$${newLiquidationPriceLabel}`}
              description={`${changeInLiquidationPriceLabelWithUSD}`}
              endDescription={`from $${prevLiquidationPriceLabel}`}
            />
          </BorrowReviewBox>
          <Block
            className={css({
              border: `1px solid ${theme.colors.borderOpaque}`,
              borderRadius: theme.borders.radius300,
              overflow: "hidden",
            })}
          >
            <BorrowReviewBox backgroundColor="transparent">
              <BorrowReviewListItem
                header={
                  strategy ===
                  BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
                    ? "Collateral to be deposited"
                    : "Collateral to withdraw"
                }
                endHeader={
                  <>
                    {updateCollateralAmount} <CurrencySymbol token="mBTC" />
                    <ParagraphSmall
                      color={theme.colors.contentSecondary}
                      marginTop={theme.sizing.scale100}
                    >
                      {collateralPriceLabelWithUSD}
                    </ParagraphSmall>
                  </>
                }
              />

              <Divider $size="cell" />
              <Block
                className={css({
                  display: "flex",
                  justifyContent: "space-between",
                  padding: `${theme.sizing.scale500} ${theme.sizing.scale600} ${theme.sizing.scale400}`,
                })}
              >
                <ParagraphSmall color={theme.colors.contentSecondary}>
                  Transaction gas fee
                </ParagraphSmall>
                <Block display="flex" alignItems="center">
                  <GasFee
                    color={theme.colors.contentTertiary}
                    size="14px"
                    className={css({
                      marginBottom: "2px",
                    })}
                  />
                  <ParagraphSmall marginLeft={theme.sizing.scale100}>
                    {"<0.000001"} <CurrencySymbol token="mBTC" />
                  </ParagraphSmall>
                </Block>
              </Block>
            </BorrowReviewBox>
          </Block>
        </Block>
        <BorrowTerms
          dataAttr={
            strategy === BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
              ? "deposit_collateral-step_3"
              : "withdraw_collateral-step_3"
          }
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
          onSubmit={onSubmit}
          label={`${
            strategy === BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
              ? "Deposit"
              : "Withdraw"
          } ${updateCollateralAmount} ${getCurrencySymbol("mBTC")}`}
        />
      </Block>
    </Block>
  )
}
